import Head from 'next/head';
import React from 'react';
import { renderJsonLdScript, getFaqStructuredData } from 'libs/universal/structuredData';

const BASE_URL = 'https://www.zola.com';

const FAQ_LD = [
  {
    question: `What are you supposed to write on a wedding invitation?`,
    answer: `The basic info includes: who’s getting married, when and where the wedding is, the wedding’s tone and formality, and how to RSVP. You can also include the wedding’s hosts (usually the bride and/or groom’s parents). For more, head to our <a href="${BASE_URL}/expert-advice/invites-paper">Expert Advice</a> section.`,
  },
  {
    question: `What if I need help with how to word my wedding invitation?`,
    answer: `We’re happy to help! There are so many ways to word your invitations and we make it easy to customize your cards at Zola, whether you want to go more formal, casual, or entirely whimsical. For tips and inspiration, visit our <a href="${BASE_URL}/expert-advice/invites-paper">Expert Advice</a> section.`,
  },
  {
    question: `How do you address wedding invitations?`,
    answer: `The way you address invitations is crucial not only for etiquette’s sake, but for logistical reasons, too. To make things easier, we format all your addresses and print them on your envelopes for free. You can edit our formatting anytime. Visit our <a href="${BASE_URL}/expert-advice/invites-paper/invitation-wording/how-to-address-wedding-invitations">Expert Advice post</a> for examples and tips.`,
  },
  {
    question: `What does the “M” mean on a wedding RSVP?`,
    answer: `This is typically found included on formal wedding invitations and response cards. The “M” followed by a blank line on a response card is where guests should fill in their title—like “Mx.,” “Mrs.,” “Miss,” or “Mr.”—followed by their name.`,
  },
  {
    question: `How much do wedding invitations cost?`,
    answer: `Fact #1: If you want people to come to your wedding, you have to let them know about it. Fact #2: Wedding expenses add up faster than ants on a forgotten lollipop. At Zola, we make it easy to get the word out with beautiful, custom, high-quality invitations starting at $1.99 each.`,
  },
  {
    question: `When should you send out save the dates and wedding invitations?`,
    answer: `We recommend sending save the dates up to six months ahead of your wedding date, or up to a year ahead of your wedding date if you’re hosting a destination wedding. Wedding invitations should go out up to eight weeks before your wedding. For more, visit our <a href="${BASE_URL}/expert-advice/exactly-when-to-send-save-the-dates-invites-and-thank-yous">Expert Advice post</a>.`,
  },
  {
    question: `How do you write a thank you card?`,
    answer: `The general rules are: be specific, make it personal, be yourself, write clearly, and share the responsibility (you and your S.O. should both sign!). <a href="${BASE_URL}/wedding-registry">Registering at Zola</a>, too? We track all gifts you receive along with your guests’ names and addresses to speed up this task. For more tips, visit this <a href="${BASE_URL}/expert-advice/how-to-write-wedding-thank-you-cards">Expert Advice post</a>.`,
  },
  {
    question: `What are all the types of wedding paper we need?`,
    answer: `The basics are save the dates, wedding invitations, and thank you cards. For the big day, many couples print programs for the ceremony that list out the bridal party and details like readings and song names. Menus are a lovely touch, too. You can get all of this paper at Zola, and even create a matching suite.`,
  },
];

const title = 'Wedding Paper, Stationary, Digital Invites, Save the Dates, & More';
const description =
  'Your one-stop destination for all wedding paper, print and digital. Explore curated collections as well as customizable designs, textures, and colors. ';

export default function Header({ page = 'paper' }: { page?: string }): JSX.Element {
  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={`${BASE_URL}/wedding-planning/${page}`} />
      <meta property="og:url" content={`${BASE_URL}/wedding-planning/${page}`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {renderJsonLdScript(getFaqStructuredData(FAQ_LD))}
    </Head>
  );
}
